import MicrophoneIcon from "@uui/Microphone01";
import { find } from "lodash/fp";

import { Box, BoxProps, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import { MultipleTimeSeriesTooltipData } from "@app/analytics/MeetingReport/components/MultipleTimeSeriesChart";
import { titleize } from "@app/utils/utils";

import { Column, Row } from "@rui/Stack";

const MAX_SPEAKER_LENGTH = 30;
export const TOOLTIP_WIDTH = 250;

const Text = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 500,
  fontFamily: theme.typography.fontFamilySecondary,
}));

type IndicatorProps = BoxProps & { color: React.CSSProperties["color"] };

const Indicator = styled(({ color: _, ...props }: IndicatorProps) => (
  <Box {...props} />
))(({ color }) => ({
  height: "8px",
  width: "8px",
  margin: "0 1px", // to make the indicator the same width as the microphone icon
  border: `2px solid ${color}`,
  borderRadius: "50%",
}));

// mui Divider component doesn't work inside visx tooltips, so make our own
const Divider = styled(Box)(({ theme }) => ({
  height: "5px",
  width: "100%",
  marginBottom: "4px",
  borderBottom: `1px solid ${theme.palette.shades.neutral[800]}`,
}));

const getSpeakerValue = (speakers: string[]) => {
  const allSpeakers = speakers.join(", ");

  if (allSpeakers.length > MAX_SPEAKER_LENGTH) {
    let speakerValue = speakers[0];
    let currentIndex = 1;
    while (speakerValue.length < MAX_SPEAKER_LENGTH) {
      const potentialSpeakerValue = [speakerValue, speakers[currentIndex]].join(
        ", "
      );
      if (potentialSpeakerValue.length > MAX_SPEAKER_LENGTH) {
        return `${speakerValue}, +${speakers.length - currentIndex}`;
      }
      speakerValue += `, ${speakers[currentIndex]}`;
      currentIndex++;
    }
    return speakerValue;
  }

  return allSpeakers;
};

const DeepDiveChartTooltip = ({
  additionalData,
  timestamp,
  indicators,
}: MultipleTimeSeriesTooltipData) => {
  const speakers = find({ id: "speakers" }, additionalData);

  return (
    <Column variant="left" sx={{ p: "9px 13px", width: `${TOOLTIP_WIDTH}px` }}>
      <Text>{timestamp}</Text>
      <Row variant="left" spacing={1}>
        <MicrophoneIcon
          sx={{ fontSize: "10px", color: "shades.emerald.400" }}
        />
        <Text>
          {speakers
            ? getSpeakerValue(speakers.value as string[])
            : "No speakers"}
        </Text>
      </Row>
      <Divider>&nbsp;</Divider>
      {indicators?.map((indicator) => (
        <Row variant="left" spacing={1} key={indicator.id}>
          <Indicator color={indicator.color}>&nbsp;</Indicator>
          <Text>
            {indicator.id === "meetingScore"
              ? "Read Score"
              : titleize(indicator.id)}
            : {indicator.value}
          </Text>
        </Row>
      ))}
    </Column>
  );
};

export default DeepDiveChartTooltip;
